import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type QueryKey,
} from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { RuleMetadata } from "@shared";

// ═══════════════════════════════════════════════════════════
// Rules Hooks — TanStack Query wrappers for /api/rules
// ═══════════════════════════════════════════════════════════

export interface RuleListResponse {
  data: RuleMetadata[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RuleListParams {
  page?: number;
  pageSize?: number;
  activeOnly?: boolean;
}

function buildRuleListUrl(params: RuleListParams): string {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.pageSize) qs.set("pageSize", String(params.pageSize));
  if (params.activeOnly === false) qs.set("activeOnly", "false");
  const str = qs.toString();
  return `/api/rules${str ? `?${str}` : ""}`;
}

// ── List all rules ────────────────────────────────────────
export function useRules(
  params: RuleListParams = {},
  options?: Partial<UseQueryOptions<RuleListResponse>>,
) {
  return useQuery({
    queryKey: queryKeys.rules.list(params),
    queryFn: () => api.raw<RuleListResponse>(buildRuleListUrl(params)),
    ...options,
  });
}

// ── Get single rule ───────────────────────────────────────
export function useRule(id: number | undefined) {
  return useQuery({
    queryKey: queryKeys.rules.detail(id!),
    queryFn: () => api.get<RuleMetadata>(`/api/rules/${id}`),
    enabled: !!id,
  });
}

// ── Create rule ───────────────────────────────────────────
export function useCreateRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post<RuleMetadata>("/api/rules", data),
    onSuccess: (createdRule) => {
      queryClient.setQueryData(queryKeys.rules.detail(createdRule.id), createdRule);
      queryClient.invalidateQueries({ queryKey: queryKeys.rules.all }); // Sync completed on the server before this response arrived — invalidate settings
      // so WebhookEventsBanner picks up the updated registeredEvents immediately.
      queryClient.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });
}

// ── Update rule (in-place) ────────────────────────────────
export function useUpdateRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      api.put<RuleMetadata>(`/api/rules/${id}`, data),
    onSuccess: (updatedRule, { id }) => {
      // Set the detail cache directly — no refetch needed since server returned full rule.
      queryClient.setQueryData(queryKeys.rules.detail(id), updatedRule);
      // Only invalidate list queries (["rules","list",...]), not detail queries.
      // Invalidating queryKeys.rules.all would also mark the detail stale,
      // triggering a redundant entity.item.get B24 call.
      queryClient.invalidateQueries({ queryKey: ["rules", "list"] }); // Sync completed on the server before this response arrived — invalidate settings
      // so WebhookEventsBanner picks up the updated registeredEvents immediately.
      queryClient.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });
}

// ── Deactivate rule ───────────────────────────────────────
export function useDeactivateRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ id: number; deactivated: boolean }>(`/api/rules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rules.all }); // Sync completed on the server before this response arrived — invalidate settings
      // so WebhookEventsBanner picks up the updated registeredEvents immediately.
      queryClient.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });
}

// ── Toggle rule active state (optimistic for both directions) ─────────
//
// Enable (false→true): calls POST /api/rules/:id/activate.
//   Optimistic update flips isActive immediately; invalidates on success.
//
// Disable (true→false): calls DELETE /api/rules/:id (soft deactivate).
//   Optimistic update flips isActive immediately; invalidates on success.
//   Rolls back on error.
//
export function useToggleRule() {
  const queryClient = useQueryClient();

  return useMutation<
    { id: number; activated?: boolean; deactivated?: boolean },
    Error,
    { id: number; isActive: boolean },
    { snapshots: Array<[QueryKey, RuleListResponse | undefined]> }
  >({
    mutationFn: async ({ id, isActive }) => {
      if (isActive) {
        // Enabling — dedicated activate endpoint (no full body needed)
        return api.post<{ id: number; activated: boolean }>(`/api/rules/${id}/activate`, {});
      } else {
        // Disabling — soft DELETE
        return api.delete<{ id: number; deactivated: boolean }>(`/api/rules/${id}`);
      }
    },

    // Optimistic update for both directions
    onMutate: async ({ id, isActive }) => {
      // Cancel any in-flight rule list fetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.rules.all });

      // Snapshot only list cache entries (exclude detail entries — they have a different shape).
      // Use a 2-element prefix so we match ALL list queries regardless of their params object
      // (e.g. ["rules","list",{activeOnly:false}]) — queryKeys.rules.list() returns
      // ["rules","list",undefined] which would NOT match due to TQ prefix semantics.
      const listQueries = queryClient.getQueriesData<RuleListResponse>({
        queryKey: ["rules", "list"],
      });

      const snapshots: Array<[QueryKey, RuleListResponse | undefined]> = listQueries.map(
        ([key, data]) => [key, data],
      );

      // Apply optimistic update — flip isActive on the toggled row
      for (const [key, data] of listQueries) {
        if (!data?.data) continue;
        queryClient.setQueryData<RuleListResponse>(key, {
          ...data,
          data: data.data.map((rule) => (rule.id === id ? { ...rule, isActive } : rule)),
        });
      }

      return { snapshots };
    },

    onError: (_err, _vars, context) => {
      // Rollback all optimistic updates on error
      if (context?.snapshots) {
        for (const [key, snapshot] of context.snapshots) {
          queryClient.setQueryData(key, snapshot);
        }
      }
    },

    onSuccess: () => {
      // Invalidate after both activate and deactivate. The optimistic update already
      // flipped the UI instantly; this background refetch syncs server state.
      queryClient.invalidateQueries({ queryKey: queryKeys.rules.all });
      // Sync completed on the server before this response arrived — invalidate settings
      // so WebhookEventsBanner picks up the updated registeredEvents immediately.
      queryClient.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });
}

// ── Permanently delete an inactive rule from B24 Data Storage ─────────────────
export function useHardDeleteRule() {
  const queryClient = useQueryClient();

  return useMutation<{ id: number; deleted: boolean }, Error, number>({
    mutationFn: (id: number) =>
      api.delete<{ id: number; deleted: boolean }>(`/api/rules/${id}/permanent`),
    onSuccess: (_data, id) => {
      // Remove deleted rule from all list caches immediately.
      // Use 2-element prefix to match ALL list queries (any params object).
      const listQueries = queryClient.getQueriesData<RuleListResponse>({
        queryKey: ["rules", "list"],
      });
      for (const [key, data] of listQueries) {
        if (!data?.data) continue;
        queryClient.setQueryData<RuleListResponse>(key, {
          ...data,
          data: data.data.filter((rule) => rule.id !== id),
          total: Math.max(0, (data.total ?? 1) - 1),
        });
      }
      // Also remove the detail cache entry if present
      queryClient.removeQueries({ queryKey: queryKeys.rules.detail(id) });
    },
  });
}
